<template>
  <div class="card-editor-container">
    <div class="card-editor">
      <!-- Header -->
      <div class="header-row">
        <div>
          <h1 class="green-heading">{{ $t('cardEditor.heading') }}</h1>
          <p class="subtitle">{{ $t('cardEditor.subtitle') }}</p>
        </div>
      </div>

      <p v-if="successMessage" class="banner banner-success" role="status">{{ successMessage }}</p>
      <p v-if="errorMessage" class="banner banner-error" role="alert">{{ errorMessage }}</p>

      <!-- Toolbar -->
      <div class="toolbar">
        <div class="form-field toolbar-pick">
          <label :for="ids.picker">{{ $t('cardEditor.toolbar.editExisting') }}</label>
          <select :id="ids.picker" v-model.number="selectedCardId" @change="onPickCard" :disabled="loading">
            <option :value="null">{{ $t('cardEditor.toolbar.selectPlaceholder') }}</option>
            <option v-for="c in cards" :key="c.id" :value="c.id">
              {{ formatCardOption(c) }}
            </option>
          </select>
        </div>
        <button type="button" class="btn btn-secondary" @click="onNewCard">
          {{ $t('cardEditor.toolbar.newCard') }}
        </button>
      </div>

      <!-- Edit banner -->
      <div v-if="bannerVisible" class="edit-banner">
        <template v-if="isCreatingNew">
          {{ $t('cardEditor.banner.creatingNew') }}
        </template>
        <template v-else-if="selectedCardId">
          {{ $t('cardEditor.banner.editing') }}
          <strong>{{ formatCardId(selectedCardId) }}</strong>
          <span class="status-badge" :class="`status-${currentStatus}`">{{ currentStatus }}</span>
          <span v-if="loadingCard" class="banner-loading">{{ $t('cardEditor.loadingCard') }}</span>
        </template>
      </div>

      <!-- Form -->
      <form class="card-form" @submit.prevent="onSave" novalidate ref="form">
        <!-- Card type + status -->
        <div class="form-row form-row-2">
          <div class="form-field">
            <label :for="ids.cardType">
              {{ $t('cardEditor.fields.cardTypeLabel') }} <span class="required">*</span>
            </label>
            <select :id="ids.cardType" v-model="form.cardType" required>
              <option v-for="t in CARD_TYPES" :key="t" :value="t">{{ t }}</option>
            </select>
          </div>
          <!-- Status gates the questionnaire picker (ContentCardBrowser shows
               only APPROVED, plus legacy 'active'). Without this control a new
               card stayed DRAFT for good and could never be added anywhere. -->
          <div class="form-field">
            <label :for="ids.status">{{ $t('cardEditor.fields.statusLabel') }}</label>
            <select :id="ids.status" v-model="form.status">
              <option v-for="s in CARD_STATUSES" :key="s" :value="s">{{ s }}</option>
            </select>
            <p class="field-hint">{{ $t('cardEditor.fields.statusHint') }}</p>
          </div>
        </div>

        <!-- One language switch for all four text blocks, the same pattern
             as the question editors. Sticky so it stays reachable while
             scrolling down to BODY_3; the dots flag which languages have
             any text yet. -->
        <div class="locale-bar">
          <LocaleSelector
            v-model="activeLang"
            :locales="LOCALES"
            :labels="LOCALE_LABELS"
            :filled-fn="isLocaleFilled"
            :hint="$t('cardEditor.fields.anyLanguage')"
            :aria-label="$t('cardEditor.fields.languageAria')"
          />
        </div>

        <!-- TITLE -->
        <fieldset class="form-fieldset">
          <legend>
            TITLE <span class="hint">{{ $t('cardEditor.fields.typeText') }}</span>
          </legend>
          <div class="form-field">
            <label :for="`${ids.title}-${activeLang}`">
              <span class="lang-tag">{{ LOCALE_LABELS[activeLang] }}</span>
            </label>
            <input
              :id="`${ids.title}-${activeLang}`"
              type="text"
              maxlength="200"
              :placeholder="$t('cardEditor.placeholders.title')"
              v-model="form.text.TITLE[activeLang]"
            />
          </div>
          <p v-if="titleError" class="field-error">{{ titleError }}</p>
        </fieldset>

        <!-- BODY_1 -->
        <fieldset class="form-fieldset">
          <legend>
            BODY_1 <span class="hint">{{ $t('cardEditor.fields.typeText') }}</span>
          </legend>
          <div class="form-field">
            <label :for="`${ids.body1}-${activeLang}`">
              <span class="lang-tag">{{ LOCALE_LABELS[activeLang] }}</span>
            </label>
            <textarea
              :id="`${ids.body1}-${activeLang}`"
              rows="4"
              :placeholder="$t('cardEditor.placeholders.body1')"
              v-model="form.text.BODY_1[activeLang]"
            ></textarea>
          </div>
        </fieldset>

        <!-- IMAGE_1 -->
        <fieldset class="form-fieldset">
          <legend>
            IMAGE_1 <span class="hint">{{ $t('cardEditor.fields.typeImage') }}</span>
          </legend>
          <div class="image-block" :class="{ 'is-empty': !form.image1Id }">
            <div class="image-thumb-wrap">
              <img
                v-if="image1Record"
                :src="thumbUrlFor(image1Record)"
                :alt="thumbAltFor(image1Record)"
                class="image-thumb"
              />
              <div v-else class="image-placeholder">
                <div class="image-placeholder-icon">🖼</div>
                <div class="image-placeholder-text">{{ $t('cardEditor.image.noImageSelected') }}</div>
              </div>
            </div>
            <div class="image-block-meta">
              <div v-if="image1Record" class="image-id-display">
                {{ imageDisplayLabel(image1Record) }}
              </div>
              <div v-else class="hint">{{ $t('cardEditor.image.noImageSelected') }}</div>
              <div class="image-buttons">
                <button
                  type="button"
                  class="btn btn-secondary btn-small"
                  @click="onPickOrChangeImage('image1')"
                >
                  {{ form.image1Id ? $t('cardEditor.image.changeImage') : $t('cardEditor.image.pickImage') }}
                </button>
                <button
                  v-if="form.image1Id"
                  type="button"
                  class="btn-link"
                  @click="form.image1Id = null"
                >
                  {{ $t('cardEditor.image.remove') }}
                </button>
              </div>
            </div>
          </div>
        </fieldset>

        <!-- BODY_2 -->
        <fieldset class="form-fieldset">
          <legend>
            BODY_2 <span class="hint">{{ $t('cardEditor.fields.typeText') }}</span>
          </legend>
          <div class="form-field">
            <label :for="`${ids.body2}-${activeLang}`">
              <span class="lang-tag">{{ LOCALE_LABELS[activeLang] }}</span>
            </label>
            <textarea
              :id="`${ids.body2}-${activeLang}`"
              rows="4"
              :placeholder="$t('cardEditor.placeholders.body2')"
              v-model="form.text.BODY_2[activeLang]"
            ></textarea>
          </div>
        </fieldset>

        <!-- IMAGE_2 -->
        <fieldset class="form-fieldset">
          <legend>
            IMAGE_2 <span class="hint">{{ $t('cardEditor.fields.typeImage') }}</span>
          </legend>
          <div class="image-block" :class="{ 'is-empty': !form.image2Id }">
            <div class="image-thumb-wrap">
              <img
                v-if="image2Record"
                :src="thumbUrlFor(image2Record)"
                :alt="thumbAltFor(image2Record)"
                class="image-thumb"
              />
              <div v-else class="image-placeholder">
                <div class="image-placeholder-icon">🖼</div>
                <div class="image-placeholder-text">{{ $t('cardEditor.image.noImageSelected') }}</div>
              </div>
            </div>
            <div class="image-block-meta">
              <div v-if="image2Record" class="image-id-display">
                {{ imageDisplayLabel(image2Record) }}
              </div>
              <div v-else class="hint">{{ $t('cardEditor.image.noImageSelected') }}</div>
              <div class="image-buttons">
                <button
                  type="button"
                  class="btn btn-secondary btn-small"
                  @click="onPickOrChangeImage('image2')"
                >
                  {{ form.image2Id ? $t('cardEditor.image.changeImage') : $t('cardEditor.image.pickImage') }}
                </button>
                <button
                  v-if="form.image2Id"
                  type="button"
                  class="btn-link"
                  @click="form.image2Id = null"
                >
                  {{ $t('cardEditor.image.remove') }}
                </button>
              </div>
            </div>
          </div>
        </fieldset>

        <!-- BODY_3 -->
        <fieldset class="form-fieldset">
          <legend>
            BODY_3 <span class="hint">{{ $t('cardEditor.fields.typeText') }}</span>
          </legend>
          <div class="form-field">
            <label :for="`${ids.body3}-${activeLang}`">
              <span class="lang-tag">{{ LOCALE_LABELS[activeLang] }}</span>
            </label>
            <textarea
              :id="`${ids.body3}-${activeLang}`"
              rows="4"
              :placeholder="$t('cardEditor.placeholders.body3')"
              v-model="form.text.BODY_3[activeLang]"
            ></textarea>
          </div>
        </fieldset>

        <!-- Actions -->
        <div class="form-actions">
          <button type="button" class="btn btn-danger" @click="onDelete" :disabled="saving || selectedCardId == null">
            {{ $t('cardEditor.actions.delete') }}
          </button>
          <div class="form-actions-right">
            <button type="button" class="btn btn-secondary" @click="onPreview" :disabled="saving">
              {{ $t('cardEditor.actions.preview') }}
            </button>
            <button type="submit" class="btn btn-primary" :disabled="saving">
              {{ saving ? $t('cardEditor.actions.saving') : $t('cardEditor.actions.saveChanges') }}
            </button>
          </div>
        </div>
      </form>

      <ImagePicker
        v-if="pickerOpen"
        :image-cache="imageCache"
        :target="pickerTarget"
        :initial-image-id="pickerInitialId()"
        @picked="onImagePicked"
        @cancel="onPickerCancel"
      />

      <ConfirmDialog
        v-if="confirmDeleteOpen"
        :title="$t('cardEditor.deleteDialog.title')"
        :message="$t('cardEditor.deleteDialog.message')"
        :confirm-label="$t('cardEditor.deleteDialog.confirm')"
        :cancel-label="$t('cardEditor.deleteDialog.cancel')"
        variant="danger"
        @confirm="onDeleteConfirmed"
        @cancel="onDeleteCancel"
      />

      <CardPreview
        v-if="previewOpen"
        :form="form"
        :initial-lang="activeLang"
        :image-cache="imageCache"
        @close="onPreviewClose"
      />
    </div>
  </div>
</template>

<script>
import i18n from '@/i18n';
import { listCards, fetchCard, saveCard, deleteCard } from '@/services/cardApi';
import { fetchImages } from '@/services/imageApi';
import { imageUrl, pickLocaleString, altTextFor } from '@/services/imageHelpers';
import ImagePicker from '@/components/image_picker/ImagePicker.vue';
import ConfirmDialog from '@/components/common/ConfirmDialog.vue';
import CardPreview from '@/components/card_preview/CardPreview.vue';
import LocaleSelector from '@/components/admin/LocaleSelector.vue';
import { SUPPORTED_LOCALES, LOCALE_LABELS } from '@/components/admin/_data/questions';

// SECTION_COMPLETE joined the bank as a fifth type during the junction
// Step 2 cutover. Same blocks shape (TITLE, BODY_1, IMAGE_1, BODY_2,
// IMAGE_2, BODY_3) and no special business logic — Tamara uses it for
// between-section congratulations screens.
const CARD_TYPES = ['FACTOID', 'RECIPE', 'TIP', 'SECTION_COMPLETE'];
// Mirrors ALLOWED_STATUSES in backend/src/crud/cards.py.
const CARD_STATUSES = ['DRAFT', 'PENDING', 'APPROVED', 'REJECTED', 'ARCHIVED'];
const LOCALES = SUPPORTED_LOCALES;
const TEXT_KINDS = ['TITLE', 'BODY_1', 'BODY_2', 'BODY_3'];

// The form owns every locale of every text block, keyed by block kind:
// form.text.TITLE.de, form.text.BODY_2.fr and so on. Inputs bind to
// form.text[kind][activeLang]. Values are '' in the form (so v-model has a
// string to bind) and become null on save when blank.
function blankText() {
  return Object.fromEntries(LOCALES.map(loc => [loc, '']));
}

function blankForm() {
  return {
    cardType: 'FACTOID',
    status: 'DRAFT',
    text: Object.fromEntries(TEXT_KINDS.map(kind => [kind, blankText()])),
    image1Id: null,
    image2Id: null,
  };
}

// Read all four locales of a card's TITLE/BODY block, '' where absent.
// Tolerates the sparse two-block section_complete rows, whose missing
// blocks simply come back blank and are written out densely on save.
function readText(card, kind) {
  const t = card?.blocks?.find(b => b.kind === kind)?.text || {};
  return Object.fromEntries(LOCALES.map(loc => [loc, typeof t[loc] === 'string' ? t[loc] : '']));
}

// Form text → wire text: every locale key present, blanks as null, which
// is the dense shape _validate_blocks expects.
function toWireText(text) {
  return Object.fromEntries(LOCALES.map(loc => {
    const v = text?.[loc];
    return [loc, typeof v === 'string' && v.trim() ? v : null];
  }));
}

// First non-empty locale of a text object, preferring `locale`, then the
// runtime's own order. Used for labels only, never for what is saved.
function pickText(text, locale) {
  for (const loc of [locale, ...LOCALES]) {
    const v = text?.[loc];
    if (typeof v === 'string' && v.trim()) return v.trim();
  }
  return '';
}

// Read the image_id out of a card's IMAGE block, or null if absent.
function readImageId(card, kind) {
  const block = card?.blocks?.find(b => b.kind === kind);
  return block?.image_id ?? null;
}

export default {
  name: 'CardEditor',
  components: { ImagePicker, ConfirmDialog, CardPreview, LocaleSelector },
  data() {
    return {
      // Loaded from /cards/list on mount.
      cards: [],
      // Image lookup table for rendering image blocks: Map<id, ImageRecord>.
      imageCache: new Map(),
      // Currently-selected card id (null when the toolbar shows "— Select —"
      // OR when the user clicked "+ New card").
      selectedCardId: null,
      // The full record loaded from /cards/get/{id}. Supplies id and
      // sort_order on save and the saved status for the banner badge.
      originalCard: null,
      // True when the user clicked "+ New card" (no card loaded yet).
      isCreatingNew: false,

      form: blankForm(),

      // Language tab the text inputs are bound to. Opens on the admin's
      // own UI language, since that is most likely the one they write in.
      activeLang: LOCALES.includes(i18n.global.locale.value) ? i18n.global.locale.value : 'en',

      // Image picker state. `pickerTarget` is 'image1' | 'image2' | null.
      pickerOpen: false,
      pickerTarget: null,

      // Delete confirmation dialog state.
      confirmDeleteOpen: false,

      // Preview dialog state.
      previewOpen: false,

      // UI state
      loading: false,        // initial mount fetch
      loadingCard: false,    // fetching a single card after dropdown change
      saving: false,         // POST /cards/save in flight
      titleError: null,
      successMessage: null,
      errorMessage: null,

      CARD_TYPES,
      CARD_STATUSES,
      LOCALES,
      LOCALE_LABELS,
      ids: {
        picker: 'card-editor-picker',
        cardType: 'card-editor-card-type',
        status: 'card-editor-status',
        title: 'card-editor-title',
        body1: 'card-editor-body1',
        body2: 'card-editor-body2',
        body3: 'card-editor-body3',
      },
    };
  },
  computed: {
    currentLocale() {
      return i18n.global.locale.value;
    },
    bannerVisible() {
      return this.isCreatingNew || this.selectedCardId != null;
    },
    // The currently-loaded card's saved status (for the banner badge), as
    // opposed to form.status, which is what the next save will write.
    currentStatus() {
      return this.originalCard?.status || 'DRAFT';
    },
    // Resolve image_id → ImageRecord via the cache (or null when unbound or
    // when the referenced image was deleted from the library).
    image1Record() {
      return this.form.image1Id != null
        ? this.imageCache.get(this.form.image1Id) || null
        : null;
    },
    image2Record() {
      return this.form.image2Id != null
        ? this.imageCache.get(this.form.image2Id) || null
        : null;
    },
  },
  async mounted() {
    await this.loadInitial();
  },
  methods: {
    imageUrl,
    altTextFor,

    // For the picker dropdown's option label: " #XXXX · TYPE · Title",
    // the title in the admin's UI language where it exists, else the
    // first language that has one.
    formatCardOption(card) {
      const id = `#${String(card.id).padStart(4, '0')}`;
      const title = pickText(readText(card, 'TITLE'), this.currentLocale)
        || this.$t('cardEditor.noTitle');
      return ` ${id} · ${card.type} · ${title}`;
    },

    // Drives the dot on each language tab: filled when any text block has
    // content in that language.
    isLocaleFilled(loc) {
      return TEXT_KINDS.some((kind) => {
        const v = this.form.text[kind][loc];
        return typeof v === 'string' && v.trim();
      });
    },

    formatCardId(id) {
      return `#${String(id).padStart(4, '0')}`;
    },

    // For the populated image-block thumb. Uses the same imageUrl helper as
    // the Image Browser — the placeholder/-prefixed seed cohort gets a
    // generated SVG, real GCS files load from the bucket.
    thumbUrlFor(record) {
      return imageUrl(record);
    },
    thumbAltFor(record) {
      return altTextFor(record, this.currentLocale);
    },
    // Build the " #XXXX · Label" string shown next to a populated image
    // block, using the locale-fallback chain on the bound record.
    imageDisplayLabel(record) {
      const id = `#${String(record.id).padStart(4, '0')}`;
      const label = pickLocaleString(record, 'label', this.currentLocale)
        || this.$t('imageBrowser.noLabel');
      return ` ${id} · ${label}`;
    },

    async loadInitial() {
      this.loading = true;
      this.errorMessage = null;
      try {
        // Load cards list and image cache in parallel.
        const [cardList, imageList] = await Promise.all([
          listCards(),
          fetchImages(),
        ]);
        this.cards = cardList;
        const map = new Map();
        for (const img of imageList) map.set(img.id, img);
        this.imageCache = map;
      } catch (e) {
        this.errorMessage = this.formatErr(e);
      } finally {
        this.loading = false;
      }
    },

    async onPickCard() {
      this.titleError = null;
      this.successMessage = null;
      this.errorMessage = null;
      this.isCreatingNew = false;

      if (this.selectedCardId == null) {
        this.originalCard = null;
        this.form = blankForm();
        return;
      }

      this.loadingCard = true;
      try {
        const card = await fetchCard(this.selectedCardId);
        this.originalCard = card;
        this.populateFormFromCard(card);
      } catch (e) {
        this.errorMessage = this.formatErr(e);
      } finally {
        this.loadingCard = false;
      }
    },

    populateFormFromCard(card) {
      this.form = {
        // DB stores types lowercase ('factoid', 'recipe', 'tip',
        // 'section_complete'); the dropdown's <option value="..."> values
        // are uppercase to match the UI display label. Normalise at the
        // binding boundary so v-model finds a matching option and the
        // dropdown actually populates on edit.
        cardType: (card.type || 'factoid').toUpperCase(),
        status: this.normalizeStatus(card.status),
        text: Object.fromEntries(TEXT_KINDS.map(kind => [kind, readText(card, kind)])),
        image1Id: readImageId(card, 'IMAGE_1'),
        image2Id: readImageId(card, 'IMAGE_2'),
      };
    },

    onNewCard() {
      this.titleError = null;
      this.successMessage = null;
      this.errorMessage = null;
      this.selectedCardId = null;
      this.originalCard = null;
      this.isCreatingNew = true;
      this.form = blankForm();
    },

    onPickOrChangeImage(slot) {
      this.pickerTarget = slot;
      this.pickerOpen = true;
      // The image cache is loaded once on mount, but uploads happen on a
      // separate page (/images/upload). Refresh on open so images added
      // since this editor mounted appear without a full reload. The picker
      // re-renders reactively when imageCache is reassigned.
      this.refreshImages();
    },

    async refreshImages() {
      try {
        const imageList = await fetchImages();
        const map = new Map();
        for (const img of imageList) map.set(img.id, img);
        this.imageCache = map;
      } catch (e) {
        // Keep the cache loaded at mount; the picker still works with it.
      }
    },
    onPickerCancel() {
      this.pickerOpen = false;
      this.pickerTarget = null;
    },
    onImagePicked(imageId) {
      if (this.pickerTarget === 'image1') {
        this.form.image1Id = imageId;
      } else if (this.pickerTarget === 'image2') {
        this.form.image2Id = imageId;
      }
      this.pickerOpen = false;
      this.pickerTarget = null;
    },
    pickerInitialId() {
      if (this.pickerTarget === 'image1') return this.form.image1Id;
      if (this.pickerTarget === 'image2') return this.form.image2Id;
      return null;
    },

    onPreview() {
      this.previewOpen = true;
    },
    onPreviewClose() {
      this.previewOpen = false;
    },

    onDelete() {
      if (this.selectedCardId == null) return;
      this.confirmDeleteOpen = true;
    },

    onDeleteCancel() {
      this.confirmDeleteOpen = false;
    },

    async onDeleteConfirmed() {
      this.confirmDeleteOpen = false;
      if (this.selectedCardId == null) return;

      this.errorMessage = null;
      this.successMessage = null;
      try {
        await deleteCard(this.selectedCardId);
        // Refresh the list so the dropdown reflects the deletion.
        this.cards = await listCards();
        this.selectedCardId = null;
        this.originalCard = null;
        this.isCreatingNew = false;
        this.form = blankForm();
        this.successMessage = this.$t('cardEditor.deleted');
      } catch (e) {
        this.errorMessage = this.formatErr(e);
      }
    },

    // Normalize a content-card status to the uppercase values the save
    // endpoint accepts (DRAFT/PENDING/APPROVED/REJECTED/ARCHIVED). Legacy
    // rows were stored as lowercase 'active' — map that to APPROVED so they
    // can be re-saved without a 422.
    normalizeStatus(status) {
      const valid = ['DRAFT', 'PENDING', 'APPROVED', 'REJECTED', 'ARCHIVED'];
      const up = String(status || 'DRAFT').toUpperCase();
      if (up === 'ACTIVE') return 'APPROVED';
      return valid.includes(up) ? up : 'DRAFT';
    },
    // Compose the save body. All four locales of every text block come
    // from the form, which now edits them all.
    buildSaveBody() {
      const oc = this.originalCard;
      const t = this.form.text;
      return {
        id: oc?.id ?? null,
        // Convert back to the lowercase DB convention. The dropdown
        // value is uppercase to match the UI display label; the
        // boundary normalisation happens here on save and in
        // populateFormFromCard on load.
        type: (this.form.cardType || '').toLowerCase(),
        status: this.normalizeStatus(this.form.status),
        // sort_order: omit on create → backend computes max+1; preserve on update.
        sort_order: oc?.sort_order ?? null,
        blocks: [
          { kind: 'TITLE', text: toWireText(t.TITLE) },
          { kind: 'BODY_1', text: toWireText(t.BODY_1) },
          { kind: 'IMAGE_1', image_id: this.form.image1Id },
          { kind: 'BODY_2', text: toWireText(t.BODY_2) },
          { kind: 'IMAGE_2', image_id: this.form.image2Id },
          { kind: 'BODY_3', text: toWireText(t.BODY_3) },
        ],
      };
    },

    async onSave() {
      this.titleError = null;
      this.successMessage = null;
      this.errorMessage = null;

      // Native validation (cardType has required attr)
      const formEl = this.$refs.form;
      if (formEl && !formEl.checkValidity()) {
        formEl.reportValidity();
        return;
      }

      // Custom: TITLE must be non-empty in at least one language, any one.
      // Mirrors _validate_blocks in backend/src/crud/cards.py.
      if (!pickText(this.form.text.TITLE, this.activeLang)) {
        this.titleError = this.$t('cardEditor.validation.titleRequired');
        return;
      }

      this.saving = true;
      try {
        const payload = this.buildSaveBody();
        // Drop sort_order: null so backend assigns next-available on create
        // and preserves existing on update (when sort_order is undefined).
        if (payload.sort_order == null) delete payload.sort_order;
        if (payload.id == null) delete payload.id;

        const saved = await saveCard(payload);

        // Refresh the list so the toolbar shows new/edited cards.
        this.cards = await listCards();
        this.originalCard = saved;
        this.selectedCardId = saved.id;
        this.isCreatingNew = false;
        // Re-populate from the canonical server state (e.g. trimming, NULLs).
        this.populateFormFromCard(saved);
        this.successMessage = this.$t('cardEditor.saved', {
          cardId: this.formatCardId(saved.id),
        });
      } catch (e) {
        this.errorMessage = this.formatErr(e);
      } finally {
        this.saving = false;
      }
    },

    formatErr(e) {
      const detail = e?.response?.data?.detail;
      const msg = detail || e?.message || 'Request failed';
      return this.$t('cardEditor.error', { message: msg });
    },
  },
};
</script>

<style scoped>
.card-editor-container {
  padding: 20px;
  font-family: 'Montserrat', 'Avenir', Helvetica, Arial, sans-serif;
}

.card-editor {
  max-width: 1000px;
  margin: 0 auto;
}

.green-heading {
  color: #42b983;
  font-weight: 600;
  margin: 0 0 0.25rem 0;
}

.subtitle {
  color: #6c757d;
  font-size: 14px;
  margin: 0 0 1.5rem 0;
}

.header-row {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 16px;
  flex-wrap: wrap;
  margin-bottom: 1rem;
}

/* Banners (success/error) */

.banner {
  margin: 0 0 1rem 0;
  padding: 10px 14px;
  border-radius: 6px;
  font-size: 14px;
}
.banner-success { background: #D4EDDA; color: #155724; border: 1px solid #c3e6cb; }
.banner-error   { background: #F8D7DA; color: #721C24; border: 1px solid #f5c6cb; }

/* Toolbar */

.toolbar {
  display: flex;
  align-items: flex-end;
  gap: 12px;
  margin-bottom: 1rem;
}

.toolbar-pick {
  flex: 1 1 auto;
}

/* Edit banner */

.edit-banner {
  display: flex;
  align-items: center;
  gap: 12px;
  background: #f1f3f5;
  border-radius: 6px;
  padding: 10px 16px;
  margin-bottom: 1rem;
  font-size: 14px;
  color: #2c3e50;
}

.edit-banner strong {
  font-weight: 600;
}

.banner-loading {
  margin-left: auto;
  color: #6c757d;
  font-size: 12px;
  font-style: italic;
}

.btn:disabled {
  cursor: not-allowed;
  opacity: 0.5;
}

.card-form {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

/* Form rows + fields (label-above-control) */

.form-row {
  display: grid;
  gap: 16px;
  grid-template-columns: 1fr;
}

@media (min-width: 600px) {
  .form-row-2 {
    grid-template-columns: 1fr 1fr;
  }
}

.form-field {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.form-field label {
  font-size: 12px;
  font-weight: 600;
  color: #6c757d;
  letter-spacing: 0.3px;
  text-transform: uppercase;
}

.form-field select,
.form-field input[type="text"],
.form-field textarea {
  font-family: inherit;
  font-size: 14px;
  padding: 8px 10px;
  border: 1px solid #ced4da;
  border-radius: 6px;
  background-color: #fff;
  color: #2c3e50;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
  box-sizing: border-box;
  width: 100%;
}

.form-field select,
.form-field input[type="text"] {
  height: 38px;
}

.form-field textarea {
  resize: vertical;
  min-height: 90px;
}

.form-field select:focus,
.form-field input:focus,
.form-field textarea:focus {
  outline: none;
  border-color: #42b983;
  box-shadow: 0 0 0 3px rgba(66, 185, 131, 0.18);
}

/* Fieldsets */

.form-fieldset {
  border: 1px solid #e9ecef;
  border-radius: 8px;
  padding: 12px 16px 16px 16px;
  margin: 0;
}

.form-fieldset legend {
  padding: 0 8px;
  font-size: 13px;
  font-weight: 600;
  color: #2c3e50;
  letter-spacing: 0.3px;
}

.required { color: #e74c3c; font-weight: 700; margin-left: 2px; }

.hint {
  color: #6c757d;
  font-size: 12px;
  font-weight: 400;
  letter-spacing: normal;
  text-transform: none;
  margin-left: 6px;
}

.field-error {
  margin: 8px 0 0 0;
  color: #721C24;
  font-size: 13px;
}

.field-hint {
  margin: 2px 0 0 0;
  color: #6c757d;
  font-size: 12px;
}

/* Language switch: sticks to the top of the scroll area so it stays in
   reach from any of the four text blocks. */
.locale-bar {
  position: sticky;
  top: 0;
  z-index: 5;
  background: #fff;
  padding-top: 4px;
}

.lang-tag {
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.04em;
  color: #534AB7;
  background: rgba(83, 74, 183, 0.12);
  padding: 1px 6px;
  border-radius: 3px;
}

/* Image block */

.image-block {
  display: flex;
  gap: 16px;
  align-items: flex-start;
  flex-wrap: wrap;
}

.image-thumb-wrap {
  width: 130px;
  height: 130px;
  flex: 0 0 130px;
  border-radius: 8px;
  overflow: hidden;
  background: #f8f9fa;
}

.image-block.is-empty .image-thumb-wrap {
  border: 2px dashed #ced4da;
  background: #fdfdfd;
}

.image-thumb {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.image-placeholder {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 4px;
  color: #adb5bd;
  text-align: center;
  padding: 8px;
  box-sizing: border-box;
}

.image-placeholder-icon { font-size: 36px; line-height: 1; }
.image-placeholder-text { font-size: 11px; }

.image-block-meta {
  display: flex;
  flex-direction: column;
  gap: 8px;
  flex: 1 1 200px;
  min-width: 200px;
  padding-top: 4px;
}

.image-id-display {
  font-size: 13px;
  color: #2c3e50;
  font-weight: 500;
  word-break: break-word;
}

.image-buttons {
  display: flex;
  gap: 12px;
  align-items: center;
}

/* Status badge — same palette as Image Browser */

.status-badge {
  padding: 3px 9px;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.4px;
}
.status-DRAFT    { background: #E8E8E8; color: #555; }
.status-PENDING  { background: #FFF3CD; color: #856404; }
.status-APPROVED { background: #D4EDDA; color: #155724; }
.status-REJECTED { background: #F8D7DA; color: #721C24; }
.status-ARCHIVED { background: #E2E3E5; color: #383D41; }

/* Buttons */

.btn {
  font-family: inherit;
  font-size: 14px;
  font-weight: 600;
  padding: 10px 24px;
  border-radius: 6px;
  border: none;
  cursor: pointer;
  transition: background-color 0.15s ease, color 0.15s ease;
  letter-spacing: 0.3px;
}

.btn-primary { background: #42b983; color: #fff; }
.btn-primary:hover { background: #36a273; }
.btn-primary:focus-visible { outline: 2px solid #2E7D32; outline-offset: 2px; }

.btn-secondary {
  background: #fff;
  color: #2c3e50;
  border: 1px solid #ced4da;
}
.btn-secondary:hover { background: #f1f3f5; }
.btn-secondary:focus-visible { outline: 2px solid #42b983; outline-offset: 2px; }

.btn-danger { background: #e74c3c; color: #fff; }
.btn-danger:hover { background: #c0392b; }
.btn-danger:focus-visible { outline: 2px solid #c0392b; outline-offset: 2px; }

.btn-small {
  padding: 6px 14px;
  font-size: 13px;
}

.btn-link {
  background: none;
  border: none;
  color: #6c757d;
  font-family: inherit;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  text-decoration: underline;
  padding: 0;
}
.btn-link:hover { color: #e74c3c; }
.btn-link:focus-visible { outline: 1px solid #6c757d; outline-offset: 2px; border-radius: 2px; }

/* Form actions */

.form-actions {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
}

.form-actions-right {
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
}

/* Responsive */

@media (max-width: 600px) {
  .toolbar {
    flex-direction: column;
    align-items: stretch;
  }
  .toolbar-pick { flex: 1 1 100%; }
  .toolbar .btn { width: 100%; }

  .image-block {
    flex-direction: column;
  }
  .image-thumb-wrap {
    width: 100%;
    height: 200px;
    flex: 0 0 200px;
  }

  .form-actions {
    flex-direction: column-reverse;
    align-items: stretch;
  }
  .form-actions-right { flex-direction: column-reverse; align-items: stretch; }
  .form-actions .btn { width: 100%; }
}
</style>
